import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import { useLibrary } from '../context/LibraryContext';
import { useBooks } from '../context/BooksContext';
import './WeeklyChallenges.css';

function getWeekId() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const week = Math.ceil(((now - start) / 86400000 + start.getDay() + 1) / 7);
  return now.getFullYear() + '-W' + week;
}

const CHALLENGES = [
  { id: 'read3', emoji: '📖', title: 'Lecteur assidu', desc: 'Ouvre 3 livres différents', target: 3, type: 'books_opened' },
  { id: 'streak3', emoji: '🔥', title: 'En feu', desc: 'Maintiens un streak de 3 jours', target: 3, type: 'streak' },
  { id: 'comment2', emoji: '💬', title: 'Bavard', desc: 'Poste 2 commentaires', target: 2, type: 'comments' },
  { id: 'finish1', emoji: '🏆', title: 'Finisseur', desc: 'Termine 1 livre', target: 1, type: 'books_finished' },
];

export default function WeeklyChallenges() {
  const { user } = useUser();
  const { library, getProgress } = useLibrary();
  const { getBookById } = useBooks();
  const [streakData, setStreakData] = useState({ current: 0 });

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(doc(db, 'userLibraries', user.id), (snapshot) => {
      if (snapshot.exists()) {
        setStreakData({ current: snapshot.data().streakCurrent || 0 });
      }
    });
    return () => unsub();
  }, [user]);

  const booksFinished = library.filter(book => {
    const fullBook = getBookById(book.id);
    if (!fullBook || fullBook.chapters.length === 0) return false;
    return getProgress(book.id) >= fullBook.chapters.length - 1;
  }).length;

  const getProgress2 = (challenge) => {
    switch (challenge.type) {
      case 'books_opened': return Math.min(library.length, challenge.target);
      case 'streak': return Math.min(streakData.current, challenge.target);
      case 'books_finished': return Math.min(booksFinished, challenge.target);
      case 'comments': return Math.min(1, challenge.target); // Simplifié
      default: return 0;
    }
  };

  const weekId = getWeekId();

  return (
    <div className="challenges-section">
      <div className="challenges-header">
        <h3 className="challenges-title">🎯 Défis de la semaine</h3>
        <span className="challenges-week">{weekId}</span>
      </div>
      <div className="challenges-list">
        {CHALLENGES.map((ch, i) => {
          const progress = getProgress2(ch);
          const completed = progress >= ch.target;
          const percent = Math.round((progress / ch.target) * 100);

          return (
            <motion.div
              key={ch.id}
              className={'challenge-card ' + (completed ? 'challenge-completed' : '')}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.08 }}
            >
              <span className="challenge-emoji">{completed ? '✅' : ch.emoji}</span>
              <div className="challenge-info">
                <p className="challenge-name">{ch.title}</p>
                <p className="challenge-desc">{ch.desc}</p>
                <div className="challenge-bar">
                  <motion.div
                    className="challenge-bar-fill"
                    initial={{ width: 0 }}
                    animate={{ width: percent + '%' }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                  />
                </div>
              </div>
              <span className="challenge-count">{progress}/{ch.target}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}